import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'tb-upload-errors',
    category: 'troubleshooting',
    title: 'Proč se nahrávání nezdařilo? — vysvětlení chybových zpráv',
    summary: 'Každá červená chyba a žluté varování, s příčinou a způsobem opravy.',
    body: (
      <>
        <p>
          Když je nahrání zamítnuto, zobrazí se <strong>červený</strong> banner a seznam problémů.
          <strong> Žluté</strong> zprávy jsou pouze varování — soubor je přesto přijat. Najděte
          svou zprávu níže.
        </p>

        <h5 className="mt-3">Červené chyby (soubor odmítnut)</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Zpráva</th><th>Co to znamená &amp; jak to opravit</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><Code>Missing required column: "..."</Code></td>
              <td>Řádek záhlaví neobsahuje sloupec, který soubor potřebuje — obvykle jde o chybu
                v pravopisu nebo v použití velkých/malých písmen. Záhlaví musí přesně odpovídat,
                např. <Code>consumption_kwh</Code>, vše malými písmeny. Opravte první řádek
                a nahrajte znovu.</td>
            </tr>
            <tr>
              <td><Code>Value must be a number, got: "..."</Code></td>
              <td>Buňka, která má obsahovat číslo, obsahuje něco jiného — téměř vždy jde o
                <strong> desetinnou čárku</strong> (<Code>3,5</Code>); musí být použita tečka (<Code>3.5</Code>).
                Pokud chcete převést celý soubor najednou, přečtěte si <ArticleLink to="data-fix-commas">Oprava souboru
                používajícího čárky jako oddělovač desetinných míst</ArticleLink>. Dále odstraňte
                jednotky, mezery nebo jiný text z číselných buněk.</td>
            </tr>
            <tr>
              <td><Code>Value must be non-negative, got: ...</Code></td>
              <td>Hodnota spotřeby, výroby nebo ceny je záporná. Tyto hodnoty nemohou být menší než nula —
                záporné číslo obvykle znamená, že byly prohozeny dva sloupce.</td>
            </tr>
            <tr>
              <td><Code>Invalid timestamp format: "..."</Code></td>
              <td>Buňku s datem a časem se nepodařilo přečíst. Použijte formát <Code>2025-01-01T14:00:00Z</Code>.
                Viz <ArticleLink to="data-csv-basics">Správné vytváření souborů CSV</ArticleLink>, kde
                jsou uvedeny přijímané formáty data a vzorec pro opravu celého sloupce najednou.</td>
            </tr>
            <tr>
              <td><Code>CSV file contains no data rows</Code></td>
              <td>Soubor obsahuje pouze řádek záhlaví nebo je prázdný. Přidejte řádky s hodinovými daty.</td>
            </tr>
            <tr>
              <td><Code>period_start must be before period_end</Code></td>
              <td>(Soubory vyúčtování energie) Počáteční datum vyúčtování je stejné jako nebo pozdější než
                datum ukončení. Opravte data.</td>
            </tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Žlutá varování (soubor přesto přijat)</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Zpráva</th><th>Co to znamená</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>Extra columns found and will be ignored</Code></td><td>Váš soubor obsahuje sloupce, které platforma nepoužívá. Neškodné — jsou jednoduše přeskočeny.</td></tr>
            <tr><td><Code>... gap(s) detected in the time series</Code></td><td>Mezi prvním a posledním řádkem chybí některé hodiny. Pro co nejlepší přesnost doplňte chybějící hodiny.</td></tr>
            <tr><td><Code>timestamp(s) are not in chronological order</Code></td><td>Řádky nejsou seřazeny podle času. Seřaďte soubor podle sloupce s časovým razítkem.</td></tr>
            <tr><td><Code>has ... missing value(s)</Code></td><td>Některé buňky v číselném sloupci jsou prázdné. Doplňte je (kde je to vhodné, použijte <Code>0</Code>).</td></tr>
            <tr><td><Code>... potential outliers</Code></td><td>Několik hodnot je neobvykle vysokých nebo nízkých. Zkontrolujte je, zda nejde o překlep, jinak je ignorujte.</td></tr>
            <tr><td><Code>contains all zero values</Code></td><td>Celý sloupec je nulový. Správné pro noční solární výrobu; jinak ověřte, zda jste exportovali správná data.</td></tr>
          </tbody>
        </Table>
        <Tip>
          Většina červených chyb pochází z desetinné čárky nebo z chybně zadaných záhlaví. Začínáte-li
          ze staženého šablonového souboru, oběma předejdete — viz <em>„Správné vytváření souborů CSV"</em>.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-forecast-flat',
    category: 'troubleshooting',
    title: 'Moje prognóza vypadá plochou nebo je chybná',
    summary: 'Pokud je předpovídaná křivka plochá, kopíruje včerejšek nebo je výrazně mimo — co zkontrolovat.',
    body: (
      <>
        <p>Pokud prognóza vypadá nepřínosně, téměř vždy je příčinou jedna z těchto situací.</p>

        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Co vidíte</th><th>Nejpravděpodobnější příčina</th><th>Co udělat</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Plochá křivka</strong> — předpověď má po celém horizontu jednu hodnotu.</td>
              <td>Velmi málo vstupní historie nebo jsou vstupní data převážně nulová či opakující se jediná hodnota.</td>
              <td>Nahrajte více historie (alespoň jeden měsíc, ideálně rok). Zkontrolujte, zda soubor neobsahuje pouze jednu opakující se hodnotu.</td>
            </tr>
            <tr>
              <td><strong>„Kopie včerejška"</strong> — předpověď vypadá jako posledních 24 h, posunutých o den.</td>
              <td>Model se vrátil k naivnímu základnímu odhadu, protože data jsou příliš krátká pro vytvoření skutečného modelu.</td>
              <td>Přidejte více historie. Pro solární prognózy přidejte soubor s počasím (viz <ArticleLink to="data-weather">Soubory s meteorologickými daty</ArticleLink>).</td>
            </tr>
            <tr>
              <td><strong>Špičky / záporné hodnoty</strong> v předpovědi.</td>
              <td>Odlehlé hodnoty v historii (např. chyba měřiče) ovlivňují model.</td>
              <td>Otevřete soubor s historií, najděte podezřelý řádek, opravte ho nebo odstraňte, znovu nahrajte a spusťte.</td>
            </tr>
            <tr>
              <td><strong>Velmi široké pásmo spolehlivosti</strong>.</td>
              <td>Model si je vědom své nejistoty — obvykle krátká historie nebo neobvyklá nedávná data.</td>
              <td>Přidejte více dat; při příštím spuštění by se pásmo mělo zúžit.</td>
            </tr>
            <tr>
              <td><strong>Oznámení „Ukázková data" na stránce</strong>.</td>
              <td>Prognózovací služba nebyla dostupná nebo data byla nedostatečná — zobrazují se ilustrativní výsledky.</td>
              <td>Zkontrolujte stránku nahrávání, zda nechybí soubory; zkuste to znovu za několik minut.</td>
            </tr>
            <tr>
              <td><strong>Solární prognóza je přibližně správná, ale velikost je špatná</strong>.</td>
              <td>Kapacita FV / orientace panelů může být na aktivu nastavena chybně, nebo soubor výroby používá kWh tam, kde se očekává kW (nebo naopak).</td>
              <td>Ověřte instalovaný výkon aktiva (kWp). Ověřte, zda jednotka v souboru výroby odpovídá tomu, co platforma očekává na stránce nahrávání.</td>
            </tr>
          </tbody>
        </Table>

        <Tip>
          Užitečná kontrola: pokud předpovíte <em>minulý</em> den budovy a předpověď vůbec neodpovídá
          tomu, co se stalo, model je nedostatečně zásobený daty. Přidání histórie je téměř vždy
          účinnější než ladění nastavení.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-optimisation-infeasible',
    category: 'troubleshooting',
    title: 'Optimalizace hlásí „infeasible"',
    summary: 'Když spuštění Optimalizace nenajde žádný platný rozvrh — časté příčiny a opravy.',
    body: (
      <>
        <p>
          „Infeasible" znamená, že optimalizátor nemohl najít <em>žádný</em> rozvrh, který by
          zároveň splňoval všechna omezení. Obvykle je jedno omezení příliš přísné.
        </p>

        <h5 className="mt-3">Co zkontrolovat, v pořadí</h5>
        <ol>
          <li>
            <strong>Kapacita baterie vs. poptávka.</strong> Pokud jste přinutili baterii pokrýt
            večerní špičku, ale její kapacita je příliš malá, optimalizátor nemá platný plán.
            Zvyšte kapacitu baterie nebo zmírněte omezení.
          </li>
          <li>
            <strong>Okno připojení EV vs. požadovaná energie.</strong> Pokud EV potřebuje 40 kWh,
            ale je připojeno jen 2 hodiny při 7 kW (= maximálně 14 kWh), nelze ho nabít. Rozšiřte
            okno nebo snižte požadovaný SOC.
          </li>
          <li>
            <strong>Cíl soběstačnosti vs. výroba.</strong> Cíl „100% soběstačnosti" s malým FV
            výkonem a bez povoleného odběru ze sítě je nesplnitelný. Snižte cíl nebo povolte
            určitý odběr.
          </li>
          <li>
            <strong>Meze min./max. SOC.</strong> Pokud je minimální SOC vyšší než maximální SOC
            (nebo obě hodnoty jsou na stejné úrovni), není prostor pro provoz. Zkontrolujte obě
            meze.
          </li>
          <li>
            <strong>Záporné ceny tarifu.</strong> Některé exporty vykazují záporné velkoobchodní
            ceny na reálných trzích. Pokud nastavíte pravidlo „export ≥ 0", tyto hodiny se stanou
            nesplnitelnými. Pravidlo odstraňte nebo soubor tarifu předfiltrujte.
          </li>
          <li>
            <strong>Chybějící data v horizontu.</strong> Mezera v souboru zatížení nebo tarifu
            v rámci optimalizačního horizontu může narušit řešič. Znovu nahrajte soubor
            s vyplněnou mezerou (použijte hodnotu z předchozí hodiny nebo 0).
          </li>
        </ol>

        <h5 className="mt-3">Jak rychle diagnostikovat</h5>
        <p>
          Spusťte znovu s cílem <strong>„Minimalizovat náklady"</strong> a <em>bez</em> pevného
          cíle soběstačnosti / vlastní spotřeby. Pokud to uspěje, bylo vaším cílem vazební omezení
          — zmírněte ho. Pokud stále selhává, jde o kapacitu aktiva nebo datovou mezeru.
        </p>
        <Tip>
          Snížení horizontu na polovinu (24 h → 12 h) někdy uspěje tam, kde 24 h selhává. To
          poukazuje na konkrétní časový blok, kde je problém — prozkoumejte tento blok ve svých
          datech.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-kpi-sanity-check',
    category: 'troubleshooting',
    title: 'Moje KPI vypadají příliš dobře — nebo příliš špatně',
    summary: 'Když soběstačnost dosahuje 99 %, NPV je obrovské nebo návratnost je nepravděpodobně krátká — co zkontrolovat.',
    body: (
      <>
        <p>
          KPI, které vypadá „příliš dobře na to, aby bylo pravdivé", téměř vždy pochází z jedné
          z malého souboru datových chyb. Před sdílením výsledku projděte tyto kontroly.
        </p>

        <h5 className="mt-3">Pokud je soběstačnost nebo vlastní spotřeba nepravděpodobně vysoká</h5>
        <ul>
          <li><strong>Soubor spotřeby je příliš malý.</strong> Hodnoty kW zadané do sloupce kWh (nebo naopak) způsobí, že komunita bude vypadat malá ve srovnání se svým FV výkonem. Znovu zkontrolujte jednotku na stránce nahrávání.</li>
          <li><strong>Nahrán pouze část roku.</strong> Pokud jste nahráli pouze letní měsíce, solární výroba samozřejmě pokryje většinu poptávky. Pro reprezentativní hodnotu nahrajte celý rok.</li>
          <li><strong>Chybějící budovy.</strong> Na platformě je pouze jedna ze tří budov, takže komunita vypadá menší a více pokrytá solární energií, než ve skutečnosti je.</li>
        </ul>

        <h5 className="mt-3">Pokud je NPV nebo roční úspora nepravděpodobně velká</h5>
        <ul>
          <li><strong>Tarif ve špatných jednotkách.</strong> €/MWh interpretováno jako €/kWh nafukuje úspory 1000×. Otevřete soubor tarifu — cena <Code>120</Code> by měla být <Code>0.12</Code>, pokud představuje €/kWh.</li>
          <li><strong>Příliš dlouhá životnost.</strong> 30letý horizont pro 15letou baterii přeceňuje úspory. Přizpůsobte životnost danému aktivu.</li>
          <li><strong>Diskontní sazba nastavena na 0 %.</strong> Bez diskontování se budoucí úspory počítají plně — obvykle nerealistické. Použijte 3–6 %, pokud váš finanční tým neurčí jinak.</li>
          <li><strong>Příliš agresivní předpoklad inflace.</strong> Pokud předpokládáte, že ceny elektřiny rostou 8 % ročně navždy, úspory explodují. Použijte střídmou hodnotu (1–4 %).</li>
        </ul>

        <h5 className="mt-3">Pokud výsledky vypadají nepravděpodobně špatně</h5>
        <ul>
          <li><strong>Soubor tarifu chybí nebo je celý nulový.</strong> Žádná cena = žádné úspory k započtení. Nahrajte tarif a spusťte znovu.</li>
          <li><strong>Kapacita FV ponechána na 0 kWp.</strong> Zkontrolujte aktivum na stránce aktiv komunity.</li>
          <li><strong>Chybná účinnost baterie.</strong> Výchozí hodnota 50 % by způsobila, že baterie „ztratí" polovinu každého kWh — zkontrolujte obousměrnou účinnost aktiva.</li>
          <li><strong>Soubor výroby ve špatném sloupci.</strong> Pokud jsou spotřeba a výroba prohozeny, optimalizátor vidí budovu jako nevyrábějící nic.</li>
        </ul>

        <Tip>
          Užitečná ověřovací kontrola: vydělte roční úspory ročním účtem za elektřinu komunity.
          Pokud je výsledek více než 50–70 %, něco je pravděpodobně nadhodnoceno. Reálné projekty
          komunitní energie obvykle snižují účty o 10–40 %.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-faq',
    category: 'troubleshooting',
    title: 'Často kladené dotazy',
    summary: 'Stručné odpovědi na běžné otázky.',
    body: (
      <>
        <h5>Potřebuji data za celý rok?</h5>
        <p>Celý rok (8 760 hodinových řádků) dává nejlepší výsledky, ale nástroje fungují i s méně daty.
          Pro časové soubory je vyžadován alespoň jeden den (24 řádků).</p>

        <h5 className="mt-3">Můj soubor používá jako oddělovač středník ( ; ) — je to problém?</h5>
        <p>Ne. Platforma automaticky detekuje čárky, středníky i tabulátory. Desetinný
          oddělovač uvnitř čísel musí být stále tečka.</p>

        <h5 className="mt-3">Mohu nahrát stejný typ souboru znovu?</h5>
        <p>Ano — opětovné nahrání nahradí předchozí soubor daného typu (u Day-Ahead tarifů
          se každý denní soubor uchovává zvlášť podle data).</p>

        <h5 className="mt-3">Výsledky vypadají jako ukázková data — proč?</h5>
        <p>Pokud je výpočetní služba dočasně nedostupná nebo požadovaná data ještě nebyla
          nahrána, platforma zobrazí jasně označené ilustrativní výsledky, abyste mohli
          stále prozkoumávat. Nahrajte skutečná data a spusťte nástroj znovu pro skutečné výsledky.</p>

        <h5 className="mt-3">Proč je tlačítko šedé?</h5>
        <p>Některé účty jsou jen pro čtení (role „viewer") a některé akce vyžadují nejprve
          nahrání dat. Najeďte na tlačítko nebo zkontrolujte nápovědu na stránce, abyste
          zjistili, co je potřeba.</p>
      </>
    ),
  },
]
